import { ProductForm } from "@/components/forms/product-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DealCategory } from "@/core";
import { ProductType, productDataSchema, type ProductData } from "@/core/entities/product";
import { useAirlineIconUpload } from "@/hooks";
import { useProductForm } from "@/hooks/forms/entities/use-product-form";
import { useCreateProduct } from "@/hooks/repository-hooks/product/use-product";
import { useCallback, useEffect, useState } from "react";

interface ProductCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProductCreateDialog = ({
  open,
  onOpenChange,
}: ProductCreateDialogProps) => {
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pendingLogoSave, setPendingLogoSave] = useState(false);

  const {
    isLoading: isUploading,
    uploadFile,
    url,
    error: uploadError,
    isComplete: isUploadComplete,
  } = useAirlineIconUpload();

  const createProductMutation = useCreateProduct();

  const form = useProductForm({
    mode: "create",
    defaultValues: {
      name: "",
      category: DealCategory.AirlineMiles,
      buyOrSell: ProductType.BOTH,
      minimumValueForSell: 0,
      maximumValueForSell: 0,
      minimumValueForBuy: 0,
      maximumValueForBuy: 0,
      sellPrices: [{ minRange: 0, maxRange: 0, price: 0 }],
      buyPrices: [{ minRange: 0, maxRange: 0, price: 0 }],
      statusTiers: [],
      inactive: false,
    },
  });

  const onSubmit = async () => {
    if (imageFile) {
      try {
        setPendingLogoSave(true);
        uploadFile(imageFile);
      } catch (error) {
        console.error("Failed to upload image:", uploadError, error);
      }
    } else {
      const formData = form.getValues();
      const data = productDataSchema.parse({ ...formData, logo: "" });
      await mutateProduct(data);
      onOpenChange(false);
    }
  };

  const { mutateAsync: createProduct } = createProductMutation;
  const { reset: resetForm } = form;

  const mutateProduct = useCallback(
    async (data: ProductData) => {
      const submitData = { ...data };
      if (imageFile && !url) {
        return;
      }
      try {
        await createProduct({ data: submitData });
        resetForm();
      } catch (error) {
        console.error("Failed to create product:", error);
        throw error;
      }
    },
    [imageFile, url, createProduct, resetForm],
  );

  // Save once per submit: the upload hook keeps `url` and `isComplete` set
  // after the first upload, so without this flag every later render re-saved.
  useEffect(() => {
    if (pendingLogoSave && url && isUploadComplete) {
      setPendingLogoSave(false);
      (async () => {
        const formData = form.getValues();
        const data = productDataSchema.parse({ ...formData, logo: url });
        await mutateProduct(data);
        onOpenChange(false);
        setImageFile(null);
        console.log("Image uploaded successfully");
      })();
    }
  }, [
    pendingLogoSave,
    isUploadComplete,
    url,
    form,
    onOpenChange,
    mutateProduct,
  ]);

  const handleClose = () => {
    if (form.formState.isDirty || imageFile) {
      setShowCloseAlert(true);
    } else {
      onOpenChange(false);
    }
  };

  const confirmClose = () => {
    setShowCloseAlert(false);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full md:max-w-4xl  max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
        >
          <SheetHeader className="p-0">
            <SheetTitle>Create New Product</SheetTitle>
            <SheetDescription>
              Add a new product to your inventory. Fill in all the required
              information below.
            </SheetDescription>
          </SheetHeader>
          <ProductForm
            form={form}
            onCancel={handleClose}
            onSubmit={onSubmit}
            isLoading={isUploading}
            imageUrl={url ?? ""}
            onImageChange={setImageFile}
            submitText="Create Product"
          />
        </SheetContent>
      </Sheet>

      <AlertDialog open={showCloseAlert} onOpenChange={setShowCloseAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClose}>
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
